import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";

// Reusado por /activar (modo="invite") y /restablecer (modo="reset").
export default function DefinirPassword({ modo = "invite" }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [estado, setEstado] = useState("validando"); // validando | valido | invalido | listo
  const [motivo, setMotivo] = useState(""); // no_encontrado | usado | expirado | tipo_invalido
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const esReset = modo === "reset";
  const titulo = esReset ? "Restablece tu contraseña" : "Activa tu cuenta";
  const subtitulo = esReset
    ? "Crea una nueva contraseña para tu cuenta."
    : "Define tu contraseña para activar tu cuenta y comenzar a operar.";

  useEffect(() => {
    if (!token) { setMotivo("no_encontrado"); setEstado("invalido"); return; }
    api.post("/auth/validar-token", { token })
      .then(({ data }) => {
        const ok = data.valido && (data.tipo === "reset" || data.tipo === "invite");
        if (ok) { setEstado("valido"); }
        else { setMotivo(data.motivo || "no_encontrado"); setEstado("invalido"); }
      })
      .catch(() => { setMotivo("error"); setEstado("invalido"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (pwd !== pwd2) { setError("Las contraseñas no coinciden."); return; }
    setSaving(true);
    try {
      await api.post("/auth/definir-password", { token, password: pwd });
      setEstado("listo");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (estado === "validando") {
    return (
      <AuthShell>
        <div data-testid="definir-password-validando" className="flex flex-col items-center py-6 text-slate-500">
          <Loader2 className="mb-3 h-6 w-6 animate-spin text-accent" />
          Validando enlace…
        </div>
      </AuthShell>
    );
  }

  if (estado === "invalido") {
    const INVALIDO = {
      usado: {
        h: "Este enlace ya se usó",
        p: "Tu cuenta ya fue activada con este enlace. Si fuiste tú, inicia sesión; si no reconoces esto, solicita uno nuevo.",
      },
      expirado: {
        h: "El enlace expiró",
        p: "Por seguridad, este enlace tiene vigencia limitada y ya caducó. Solicita uno nuevo para continuar.",
      },
      no_encontrado: {
        h: "Enlace no encontrado",
        p: "No encontramos este enlace. Asegúrate de copiar la dirección completa del correo, o solicita uno nuevo.",
      },
      tipo_invalido: {
        h: "Enlace no válido",
        p: "Este enlace no corresponde a esta acción. Solicita uno nuevo.",
      },
      error: {
        h: "No pudimos validar el enlace",
        p: "Hubo un problema de conexión al validar el enlace. Inténtalo de nuevo en unos minutos.",
      },
    };
    const m = INVALIDO[motivo] || INVALIDO.no_encontrado;
    return (
      <AuthShell>
        <div data-testid="definir-password-invalido" data-motivo={motivo} className="text-center">
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
          <h1 className="font-heading text-xl font-semibold text-slate-900">{m.h}</h1>
          <p className="mt-2 text-sm text-slate-500">{m.p}</p>
          <Link to="/recuperar" data-testid="ir-recuperar" className="mt-5 inline-flex">
            <Button variant="outline">Solicitar un enlace nuevo</Button>
          </Link>
          <p className="mt-4 text-sm">
            <Link to="/login" className="font-medium text-accent hover:underline">Volver a iniciar sesión</Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  if (estado === "listo") {
    return (
      <AuthShell>
        <div data-testid="definir-password-exito" className="text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <h1 className="font-heading text-xl font-semibold text-slate-900">
            {esReset ? "Contraseña actualizada" : "Cuenta activada"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">Ya puedes iniciar sesión con tu nueva contraseña.</p>
          <Button data-testid="ir-login-btn" className="mt-5 w-full bg-primary hover:bg-sidebar-hover"
            onClick={() => navigate("/login")}>
            Iniciar sesión <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{titulo}</h1>
      <p className="mt-2 text-sm text-slate-500">{subtitulo}</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <Label htmlFor="pwd" className="mb-1.5 block text-sm font-medium text-slate-700">Nueva contraseña</Label>
          <Input id="pwd" data-testid="nueva-password" type="password" value={pwd} required
            onChange={(e) => setPwd(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password" />
        </div>
        <div>
          <Label htmlFor="pwd2" className="mb-1.5 block text-sm font-medium text-slate-700">Confirmar contraseña</Label>
          <Input id="pwd2" data-testid="confirmar-password" type="password" value={pwd2} required
            onChange={(e) => setPwd2(e.target.value)} placeholder="Repite la contraseña" autoComplete="new-password" />
        </div>
        {error && (
          <div data-testid="definir-password-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <Button data-testid="definir-password-submit" type="submit" disabled={saving}
          className="w-full bg-primary hover:bg-sidebar-hover">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {esReset ? "Guardar contraseña" : "Activar cuenta"}
        </Button>
      </form>
    </AuthShell>
  );
}
